// E2E-only discovery input and room-agent refresh. The real table, selection effect,
// BridgeSelector and add store remain in charge of all selection transitions.
import { useContext, useEffect, useState } from 'react';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { Discovery } from '#~/video/components/add/source/types.js';
import type { Discovery as AnyDiscovery } from '#~/device/discovery/index.js';
import type { Producer } from '#~/video/components/add/store/types.js';
import { Context } from '#~/video/components/add/store/context.js';
import { depot } from '#~/depot/index.js';
import { useTransitoryStore } from '#~/device/transitory.js';

interface DiscoverySelectionProbe {
  stage(): void;
  refreshBridges(): void;
  snapshot(): {
    preferredId?: string | undefined;
    selectedId?: string | undefined;
    protocol?: string | undefined;
  };
  reset(): void;
  bulkSnapshot(): unknown;
  outcome(): unknown;
}

declare global {
  // eslint-disable-next-line no-var
  var __irisDiscoverySelectionProbe: DiscoverySelectionProbe | undefined;
}

export const useDiscoverySelectionFixture = (
  producer: Producer,
  agentId: Discovery['agentId'],
  preferredId: Discovery['id'] | undefined,
  selection: AnyDiscovery | undefined
): Discovery[] | undefined => {
  const addStore = useContext(Context);
  const [fixtures, setFixtures] = useState<Discovery[]>();
  const snapshot = useLatestFn(() => ({
    preferredId,
    selectedId: selection?.id,
    protocol: selection?.video?.protocol ?? selection?.control?.protocol
  }));
  const stage = useLatestFn(() => {
    // RFC 5737 host: selecting a fixture cannot contact a physical lab camera.
    const common = {
      name: 'Transport test camera',
      address: '192.0.2.10',
      agentId,
      agentIds: [agentId]
    };
    setFixtures([
      {
        ...common,
        id: 'e2e-iris-row',
        irisEnabled: true,
        video: { protocol: 'iris', host: common.address, port: 9321, name: common.name }
      },
      {
        ...common,
        id: 'e2e-ndi-row',
        video: { protocol: 'ndi', host: common.address, name: common.name }
      },
      {
        ...common,
        id: 'e2e-onvif-row',
        control: { protocol: 'onvif', host: common.address, port: 80 }
      }
    ] as Discovery[]);
  });

  useEffect(() => {
    // Renderer env comes through preload; Vite erases bare process.env reads.
    if (
      (window as unknown as { electron?: { process?: { env?: Record<string, string> } } }).electron
        ?.process?.env?.IRIS_E2E_TEST !== '1'
    )
      return;
    const probe: DiscoverySelectionProbe = {
      stage,
      snapshot,
      // Observe the real add pipeline without injecting rows or changing its routing.
      // Explicit fields exclude credentials, URXs, tokens and registry payloads.
      bulkSnapshot: () => {
        const state = addStore?.getState();
        return {
          agentId: state?.agentId,
          selectionIds: state?.source.multiSelection,
          discoveries: state?.source.bulkDiscoveries?.map((row) => ({
            id: row.id,
            host:
              row.address ??
              (row.video && 'host' in row.video ? row.video.host : undefined) ??
              (row.control && 'host' in row.control ? row.control.host : undefined),
            protocol: row.video?.protocol ?? row.control?.protocol,
            irisEnabled: row.irisEnabled === true,
            agentId: row.agentId,
            agentIds: row.agentIds
          }))
        };
      },
      outcome: () => {
        const roomId = addStore?.getState().roomId;
        const { agents, rooms } = depot.getState();
        return {
          agents: Object.values(agents).map(({ id, embedded }) => ({ id, embedded })),
          devices: Object.values((roomId && rooms[roomId]?.devices) || {}).map((device) => ({
            id: device.id,
            agentId: device.agentId,
            modelId: device.modelId,
            protocol: device.video?.config?.protocol,
            host:
              device.video?.config && 'host' in device.video.config
                ? device.video.config.host
                : undefined
          })),
          placeholders: Object.values(useTransitoryStore.getState().placeholders).map((entry) => ({
            id: entry.id,
            discoveryId: entry.discoveryId,
            kind: entry.transitoryKind,
            host: entry.address,
            agentId: entry.originalDiscovery.agentId
          }))
        };
      },
      refreshBridges: () =>
        producer((draft) => {
          // Same update as Provider receives when room agents refresh, with unchanged membership.
          draft.agentIds = [...draft.agentIds];
        }),
      reset: () => setFixtures(undefined)
    };
    globalThis.__irisDiscoverySelectionProbe = probe;
    return () => {
      if (globalThis.__irisDiscoverySelectionProbe === probe) {
        globalThis.__irisDiscoverySelectionProbe = undefined;
      }
    };
  }, [producer, stage, snapshot, addStore]);
  return fixtures;
};
